import React, { useEffect, useState } from 'react';
import { Package, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrdersPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/login?redirect=/orders');
      return;
    }

    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(response.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [token, navigate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'shipped': return 'bg-blue-100 text-blue-700';
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen py-12" data-testid="orders-page">
      <div className="px-6 md:px-12 lg:px-24">
        <h1 className="font-heading text-3xl md:text-4xl text-leather-900 mb-8">My Orders</h1>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16" data-testid="no-orders">
            <Package className="w-16 h-16 text-leather-300 mx-auto mb-4" />
            <p className="text-leather-400 text-lg mb-6">No orders yet</p>
            <button onClick={() => navigate('/shop/belts')} className="btn-primary">
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="space-y-6" data-testid="orders-list">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-cream-100 p-6"
                data-testid={`order-${order.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                  <div>
                    <p className="text-xs text-leather-400 mb-1">Order ID</p>
                    <p className="font-mono text-sm text-leather-900">{order.id.slice(0, 8)}...</p>
                  </div>
                  <div>
                    <p className="text-xs text-leather-400 mb-1">Date</p>
                    <p className="text-sm text-leather-900">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-leather-400 mb-1">Total</p>
                    <p className="font-medium text-leather-900">${order.total.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 text-xs uppercase tracking-wider ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                    <span className={`px-3 py-1 text-xs uppercase tracking-wider ${
                      order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {order.payment_status}
                    </span>
                  </div>
                </div>

                <div className="border-t border-cream-200 pt-4">
                  <p className="text-xs text-leather-400 mb-3">Items</p>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-leather-600">
                          {item.name} x {item.quantity}
                        </span>
                        <span className="text-leather-900">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
